import type { Roast } from "@/hooks/useStore";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { OverallScore } from "@/components/ScoreBadge";
import { VoteButton } from "@/components/VoteButton";
import { useApp } from "@/App";
import { MessageSquare, GitFork, ExternalLink, Clock } from "lucide-react";

interface RoastCardProps {
	roast: Roast;
}

function timeAgo(date: string): string {
	const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
	if (seconds < 60) return "just now";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `${days}d ago`;
	return `${Math.floor(days / 30)}mo ago`;
}

export function RoastCard({ roast }: RoastCardProps) {
	const { navigate } = useApp();

	return (
		<Card
			className="cursor-pointer border-border/40 bg-card/50 transition-all hover:border-border/80 hover:bg-card/80"
			onClick={() => navigate({ name: "roast", id: roast.id })}
		>
			<CardContent className="p-5">
				<div className="flex gap-4">
					<OverallScore score={roast.feedback.overallScore} />

					<div className="min-w-0 flex-1 space-y-2">
						<div className="flex items-start justify-between gap-2">
							<div className="min-w-0">
								{roast.url && (
									<div className="mb-1 flex items-center gap-1.5">
										<ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
										<span className="truncate text-sm text-muted-foreground">
											{roast.url}
										</span>
									</div>
								)}
								<p className="line-clamp-2 text-sm leading-relaxed text-foreground/90">
									"{roast.copy}"
								</p>
							</div>
						</div>

						<div className="flex flex-wrap gap-1.5">
							{Object.entries(roast.feedback)
								.filter(([key]) => !["overallScore", "summary"].includes(key))
								.map(([key, val]) => {
									const v = val as { score: number };
									const label =
										key === "trustSignals"
											? "Trust"
											: key === "cta"
												? "CTA"
												: key.charAt(0).toUpperCase() + key.slice(1);
									const color =
										v.score >= 7
											? "bg-emerald-400/10 text-emerald-400 border-emerald-400/20"
											: v.score >= 4
												? "bg-amber-400/10 text-amber-400 border-amber-400/20"
												: "bg-red-400/10 text-red-400 border-red-400/20";
									return (
										<Badge
											key={key}
											variant="outline"
											className={`text-xs font-medium ${color}`}
										>
											{label}: {v.score}/10
										</Badge>
									);
								})}
						</div>

						<div className="flex items-center gap-3 pt-1">
							<button
								onClick={(e) => {
									e.stopPropagation();
									if (roast.user) navigate({ name: "profile", id: roast.userId });
								}}
								className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
							>
								<div className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-500/20 text-[10px] font-bold text-orange-500">
									{roast.user?.username?.[0]?.toUpperCase() ?? "?"}
								</div>
								{roast.user?.username ?? "Anonymous"}
							</button>

							<span className="flex items-center gap-1 text-xs text-muted-foreground">
								<Clock className="h-3 w-3" />
								{timeAgo(roast.createdAt)}
							</span>

							<div className="ml-auto flex items-center gap-2">
								<span className="flex items-center gap-1 text-xs text-muted-foreground">
									<MessageSquare className="h-3.5 w-3.5" />
									{roast.commentCount}
								</span>
								<span className="flex items-center gap-1 text-xs text-muted-foreground">
									<GitFork className="h-3.5 w-3.5" />
									{roast.rewriteCount}
								</span>
								<div onClick={(e) => e.stopPropagation()}>
									<VoteButton
										targetId={roast.id}
										targetType="roast"
										initialCount={roast.upvotes}
										size="sm"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
